import { makeAutoObservable, runInAction } from 'mobx';
import { message } from 'odc-shared/components';
import { i18nTrans } from 'odc-shared/i18n';

import {
  CommPlatform,
  ModulePopularPostItem,
  ModuleSupportCenterCatalog,
} from '@src/api/generated/data-contracts';
import { getSupportCatalogs, getSupportPopular } from '@src/api/generated/Support';
import { normalizeLanguage } from '@src/pages/supportCenter/constants/language';

/**
 * 错误提示是否跟随 i18n。
 *
 * 官网首页暂不做 i18n（保持英文原文），只有已支持多语言的页面（帮助中心）调用时传
 * `i18nErrorText = true`。命名刻意避开 `localized`/`language`，防止与语言入参混淆。
 */
const transError = (key: string, ns: string, i18nErrorText: boolean): string =>
  i18nErrorText ? i18nTrans(key, ns) : key;

/** fetchCatalogs 的可选项 */
export interface FetchCatalogsOptions {
  /** 只取根层目录 */
  rootOnly?: boolean;
  /** 跳过缓存与在途去重，强制重新拉取 */
  force?: boolean;
  /** 内容语言，三平台同一口径 */
  language?: string;
  /** 错误提示是否跟随 i18n（帮助中心传 true；官网首页不传，保持英文原文） */
  i18nErrorText?: boolean;
}

class HelpCenterStore {
  // 目录结构数据 - 按平台区分
  // 注意：mediation 仅在 SupportCenter 详情页（URL 含 platform=mediation）按需拉取，
  // 首页 / 全局搜索 / 热门文档不感知 mediation。
  catalogsByPlatform: Record<CommPlatform, ModuleSupportCenterCatalog[]> = {
    publisher: [],
    advertiser: [],
    mediation: [],
  };
  catalogsLoading: Record<CommPlatform, boolean> = {
    publisher: false,
    advertiser: false,
    mediation: false,
  };
  catalogsMap: Map<string, ModuleSupportCenterCatalog> = new Map();

  // 已缓存目录对应的内容语言，用于判断切语言后是否要重新拉取
  catalogsLanguage: Record<CommPlatform, string> = {
    publisher: '',
    advertiser: '',
    mediation: '',
  };

  // 已缓存目录对应的 rootOnly 入参：不同 rootOnly 拿到的树不同，必须一起参与缓存比对
  catalogsRootOnly: Record<CommPlatform, boolean> = {
    publisher: false,
    advertiser: false,
    mediation: false,
  };

  // 在途请求的「缓存键」（= 语言 + rootOnly）：请求去重只对同一份数据生效，
  // 也用来丢弃已被新请求取代的旧响应，避免旧语言覆盖新语言。
  catalogsRequestKey: Record<CommPlatform, string> = {
    publisher: '',
    advertiser: '',
    mediation: '',
  };

  // 热门文章数据
  popularPosts: ModulePopularPostItem[] = [];
  popularPostsLoading = false;

  constructor() {
    makeAutoObservable(this);
  }
  /**
   * 获取目录树结构
   *
   * language 决定目录名取哪一份语言；缓存按「平台 + 语言 + rootOnly」判定，
   * 切语言后即便已有该平台数据也必须重新拉取，否则会看到上一个语言的目录名。
   */
  async fetchCatalogs(platform: CommPlatform, options: FetchCatalogsOptions = {}) {
    // rootOnly 默认 false：不传和传 false 是同一份数据，缓存键也必须相同
    const { rootOnly = false, force = false, language, i18nErrorText = false } = options;
    // 语言对三平台一致：未传 / 未启用 / 非法值一律收敛到基准语言
    const lang = normalizeLanguage(language);
    // 缓存键：语言 + rootOnly。去重、竞态判定都以它为准
    const requestKey = `${lang}\u0000${rootOnly}`;

    // 去重只对**同一份数据**生效：只按平台去重会让在途的英文请求吞掉紧接着的中文请求，
    // 页面永远停在英文（force 时不拦）
    if (!force && this.catalogsLoading[platform] && this.catalogsRequestKey[platform] === requestKey) return;

    // 不依赖数组长度：某平台目录为空是合法状态，用长度判空会让它永远不命中缓存、反复请求
    const cacheHit =
      this.catalogsLanguage[platform] === lang && this.catalogsRootOnly[platform] === rootOnly;
    if (!force && cacheHit) return;

    this.catalogsLoading[platform] = true;
    this.catalogsRequestKey[platform] = requestKey;
    try {
      const response = await getSupportCatalogs({
        platform,
        root_only: rootOnly,
        language: lang,
      });

      // 竞态保护：期间若有更新的请求（比如刚切到另一个语言）发出，本次结果作废，
      // 不允许旧语言的数据覆盖新语言的请求结果
      if (this.catalogsRequestKey[platform] !== requestKey) return;

      if (response.code === 0 && response.data?.catalogs) {
        // catalogs 是根节点数组（后端只给一个虚拟根，真实一级目录在它的 children 里）。
        // 必须写 `?.[0]?.children`：某平台一条目录都没上线时后端返回 `catalogs: []`，
        // 而空数组是 truthy —— 只判 `response.data?.catalogs` 会继续走 `[0].children`，
        // 抛 TypeError: Cannot read properties of undefined (reading 'children')。
        // 空目录是合法状态，不该提示错误。
        const children = response.data.catalogs?.[0]?.children ?? [];
        runInAction(() => {
          this.catalogsByPlatform[platform] = children;
          this.catalogsLanguage[platform] = lang;
          this.catalogsRootOnly[platform] = rootOnly;
        });
        // 目录 id / postId 与语言无关，同键覆盖即可把名称刷成新语言，无需清空整个 map
        this.setCatalogsMap(children);
      } else {
        message.error(response.msg || transError('Failed to fetch catalogs', 'helpCenter', i18nErrorText));
      }
    } catch (err) {
      // 同上：被新请求取代的旧请求，失败也不提示（提示属于已作废的那一次）
      if (this.catalogsRequestKey[platform] !== requestKey) return;
      console.error('获取目录数据失败:', err);
      message.error(transError('Something went wrong. Please try again later.', 'common', i18nErrorText));
    } finally {
      runInAction(() => {
        // 只在"自己仍是最新请求"时清 loading，否则会把还在途的新请求标记成已结束
        if (this.catalogsRequestKey[platform] === requestKey) {
          this.catalogsLoading[platform] = false;
        }
      });
    }
  }

  /**
   * 获取热门文章
   *
   * 不带 language：热门文档只出现在首页，而首页全程英文（没有语言入口），
   * 后端对应接口也未提供 language 入参。
   *
   * i18nErrorText：错误提示是否跟随 i18n（帮助中心传 true；官网首页不传）。
   */
  async fetchPopularPosts(force = false, i18nErrorText = false) {
    if (!force && this.popularPosts.length > 0) return;

    this.popularPostsLoading = true;
    try {
      const response = await getSupportPopular();

      if (response.code === 0 && response.data?.items) {
        runInAction(() => {
          this.popularPosts = response.data.items || [];
        });
      } else {
        message.error(response.msg || transError('Failed to fetch popular posts', 'helpCenter', i18nErrorText));
      }
    } catch (err) {
      console.error('获取热门文章失败:', err);
      message.error(transError('Something went wrong. Please try again later.', 'common', i18nErrorText));
    } finally {
      runInAction(() => {
        this.popularPostsLoading = false;
      });
    }
  }

  setCatalogsMap(catalogs: ModuleSupportCenterCatalog[]) {
    catalogs.forEach((catalog) => {
      if (catalog.postId) {
        this.catalogsMap.set(catalog.postId!, catalog);
      } else {
        this.catalogsMap.set(catalog.id!, catalog);
      }
      this.setCatalogsMap(catalog.children || []);
    });
  }
}

export type HelpCenterStoreType = HelpCenterStore;

export default new HelpCenterStore();